import { OrchestrationTeam, Platform } from 'common-src/models/Client';
import { PlatformStatus } from 'common-src/models/Patient';

import { availableMinutes, committedMinutes, estMinutes } from './capacity';

const TASK_TEAM_TO_ORCHESTRATION_TEAM = {
  member: OrchestrationTeam.Member,
  ops: OrchestrationTeam.Operations,
  clinical: OrchestrationTeam.Clinical,
  rcm: OrchestrationTeam.RCM,
};

const PRIORITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };

export function getStaffDisplayName(client) {
  if (typeof client?.getName === 'function') return client.getName(false, true);
  return `${client?.firstName || ''} ${client?.lastName || ''}`.trim();
}

export function getStaffInitials(client) {
  const name = getStaffDisplayName(client);
  const parts = name
    .replace(/^Dr\.\s+/i, 'Dr ')
    .trim()
    .split(/\s+/);
  if (!parts.length || !parts[0]) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function getStaffRole(client) {
  return client?.role || '';
}

function priorityRank(task) {
  return PRIORITY_RANK[task.priority || 'low'] ?? PRIORITY_RANK.low;
}

export function isEligible(client, task) {
  const requiredTeam = TASK_TEAM_TO_ORCHESTRATION_TEAM[task.team];
  if (requiredTeam == null) return false;
  return client.orchestrationTeams?.includes(requiredTeam) ?? false;
}

function getPatientRequiredPlatforms(patient) {
  const cogActive = patient?.cognitiveStatus === PlatformStatus.Active;
  const diabActive = patient?.diabetesStatus === PlatformStatus.Active;
  const cogGray = patient?.cognitiveStatus === PlatformStatus.Gray;
  const diabGray = patient?.diabetesStatus === PlatformStatus.Gray;

  if (cogActive && diabGray) return [Platform.Mindspan];
  if (diabActive && cogGray) return [Platform.Perry];
  if (cogActive && diabActive) return [Platform.Mindspan, Platform.Perry];
  return [];
}

export function eligibleStaff(task, clients, activeTeam = 'all') {
  const requiredPlatforms = getPatientRequiredPlatforms(task?.patient);
  return clients.filter((client) => {
    if (!isEligible(client, task)) return false;
    if (
      requiredPlatforms.length &&
      !requiredPlatforms.some((platform) => client.platforms?.includes(platform))
    )
      return false;
    if (activeTeam === 'all') return true;
    const scopeTeam = TASK_TEAM_TO_ORCHESTRATION_TEAM[activeTeam];
    if (scopeTeam == null) return true;
    return client.orchestrationTeams?.includes(scopeTeam) ?? false;
  });
}

export function platformEligibleStaff(task, clients) {
  const requiredPlatforms = getPatientRequiredPlatforms(task?.patient);
  if (!requiredPlatforms.length) return clients;
  return clients.filter((client) =>
    requiredPlatforms.some((platform) => client.platforms?.includes(platform)),
  );
}

export function staffForTeam(clients, activeTeam = 'all') {
  if (activeTeam === 'all') return clients;
  const scopeTeam = TASK_TEAM_TO_ORCHESTRATION_TEAM[activeTeam];
  if (scopeTeam == null) return clients;
  return clients.filter((client) => client.orchestrationTeams?.includes(scopeTeam) ?? false);
}

export function groupOwnedTasksByClientId(allTasks, clients) {
  const clientIdByName = new Map();
  clients.forEach((client) => {
    const name = getStaffDisplayName(client).toLowerCase();
    if (name) clientIdByName.set(name, client.id);
  });

  const tasksByClientId = new Map();
  allTasks.forEach((task) => {
    const owner = `${task.owner || ''}`.trim().toLowerCase();
    if (!owner || owner === 'unassigned') return;
    const clientId = clientIdByName.get(owner);
    if (clientId == null) return;
    if (!tasksByClientId.has(clientId)) tasksByClientId.set(clientId, []);
    tasksByClientId.get(clientId).push(task);
  });

  return tasksByClientId;
}

export function computeMinutesLoadByClientId(allTasks, clients, horizon = 'day') {
  const tasksByClientId = groupOwnedTasksByClientId(allTasks, clients);
  const load = {};
  clients.forEach((client) => {
    const clientTasks = tasksByClientId.get(client.id) || [];
    load[client.id] = committedMinutes(clientTasks, horizon, getStaffRole(client));
  });
  return load;
}

export function balanceAssign(
  selectedTasks,
  clients,
  loadByClientId,
  activeTeam = 'all',
  horizon = 'day',
) {
  const load = new Map();
  clients.forEach((client) => load.set(client.id, loadByClientId[client.id] ?? 0));

  const ordered = [...selectedTasks].sort((a, b) => {
    const pr = priorityRank(a) - priorityRank(b);
    if (pr !== 0) return pr;
    const da = a.due ?? '';
    const db = b.due ?? '';
    if (da !== db) return da < db ? -1 : 1;
    return `${a.id}` < `${b.id}` ? -1 : `${a.id}` > `${b.id}` ? 1 : 0;
  });

  const proposals = [];
  const unassignableTaskIds = [];
  let rrCursor = 0;

  ordered.forEach((task) => {
    const eligible = eligibleStaff(task, clients, activeTeam);
    if (!eligible.length) {
      unassignableTaskIds.push(`${task.id}`);
      return;
    }

    const withinBudget = eligible.filter((c) => {
      const projected = (load.get(c.id) ?? 0) + estMinutes(task, getStaffRole(c));
      const budget = availableMinutes(horizon, c.dailyCapacityMinutes, c.weeklyCapacityMinutes);
      return projected <= budget;
    });

    if (!withinBudget.length) {
      unassignableTaskIds.push(`${task.id}`);
      return;
    }

    const minLoad = Math.min(...withinBudget.map((c) => load.get(c.id) ?? 0));
    const tied = withinBudget
      .filter((c) => (load.get(c.id) ?? 0) === minLoad)
      .sort((a, b) => a.id - b.id);

    const chosen = tied[rrCursor % tied.length];
    rrCursor++;

    const currentOwner = `${task.owner || ''}`.trim();
    const chosenName = getStaffDisplayName(chosen);
    if (chosenName.toLowerCase() === currentOwner.toLowerCase()) return;

    proposals.push({
      taskId: `${task.id}`,
      fromOwner: currentOwner || null,
      toClientId: chosen.id,
      toDisplayName: chosenName,
    });
    load.set(chosen.id, (load.get(chosen.id) ?? 0) + estMinutes(task, getStaffRole(chosen)));
  });

  return { proposals, unassignableTaskIds };
}
